/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useState } from "react";
import { Button } from "@heroui/button";
import { Accordion, AccordionItem } from "@heroui/accordion";
import "@/styles/rich-text-content.css";
import { scroller } from "react-scroll";

interface TermsSectionProps {
  sortedTerms: {
    type: "terms_items";
    value: {
      section_title: string;
      section_content: string;
      priority: number;
    };
    id: string;
  }[];
}

const TermsSection: React.FC<TermsSectionProps> = ({ sortedTerms }) => {
  const [selectedSection, setSelectedSection] = useState<any>(
    sortedTerms[0].id,
  );

  const handleClick = (id: string) => {
    setSelectedSection(id);
    setTimeout(() => {
      scroller.scrollTo(id, {
        duration: 300,
        delay: 0,
        smooth: "easeInOutCubic",
      });
    }, 300);
  };

  return (
    <section className="flex flex-col items-center justify-center px-4 md:px-14 xl:px-[100px] max-w-[1440px] mx-auto pb-10">
      <div className="flex flex-col md:flex-row gap-5 items-start justify-start w-full">
        <div className="flex flex-col items-start justify-start gap-6 md:max-w-fit w-full md:mt-4">
          <p className="text-2xl lg:text-3xl text-center md:text-left w-full">
            Table of Contents
          </p>
          <div className="flex md:hidden flex-wrap items-center justify-center gap-4">
            {sortedTerms.map((item: any, index: any) => (
              <Button
                key={index}
                className="text-medium border-[1px]"
                radius="full"
                variant="bordered"
                onPress={() => handleClick(item.id)}
              >
                {item.value.section_title}
              </Button>
            ))}
          </div>
          <div className="hidden md:flex flex-col items-start justify-start gap-3">
            {sortedTerms.map((item: any, index: any) => (
              <Button
                key={index}
                className="text-left text-xl w-full justify-start"
                radius="sm"
                variant="light"
                onPress={() => handleClick(item.id)}
              >
                {item.value.section_title}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-start justify-start gap-6 w-full">
          <Accordion
            className="w-full"
            selectedKeys={[selectedSection]}
            showDivider={false}
            onSelectionChange={(key: any) => {
              setSelectedSection(key.currentKey);
            }}
          >
            {sortedTerms.map((item: any) => (
              <AccordionItem
                key={item.id}
                hideIndicator
                classNames={{
                  title: `text-xl ${item.id === selectedSection ? "text-ax-secondary" : ""}`,
                  trigger: "py-5 cursor-pointer",
                  base: "border-b-[1px] border-black/10",
                }}
                id={item.id}
                startContent={
                  <p
                    className={`text-xl font-semibold ${item.id === selectedSection ? "text-ax-secondary" : ""}`}
                  >
                    {item.id === selectedSection ? "—" : "+"}
                  </p>
                }
                title={item.value.section_title}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: item.value.section_content,
                  }}
                  className="flex flex-col items-start justify-start dynamic-rich-text-content pt-0 md:pt-3 pb-5 md:pb-10"
                />
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default TermsSection;
